/**
 * AssignedBlocksSurface — Pillar #2 of the driver experience.
 * ---------------------------------------------------------------------
 * Top-center pill that morphs into a full drawer listing the driver's
 * **Assigned Blocks** — every block they have claimed but NOT yet
 * started. The block currently being executed (in_progress) lives in
 * the Current Block surface at the bottom and is intentionally absent
 * from this list (the spec: "Block leaves Assigned Blocks → Becomes
 * the Current Block floating card").
 *
 * The surface only mounts when `blocks.length > 0`. Tapping the pill
 * expands the same motion element into a drawer; tapping a card or
 * action inside the drawer triggers Start / Release.
 */
import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Calendar, Clock, MapPin, Layers, Play, Trash2 } from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import { surfaceStyle, surfaceClass } from "@/shared/motion/surface";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";
import { toast } from "sonner";

const APPLE_EASE_OUT = [0.32, 0.72, 0, 1];

// Morph variants — pill (top-center, where the removed Current Block
// pill used to live) ↔ drawer (full-screen sliding from the top edge).
// Width-first on close, top-first on open. The bottom slot now belongs
// to the Current Block card / Top Opportunity card; this pill ONLY
// holds the schedule list.
const AB_VARIANTS = {
  pill: {
    width: "min(280px, calc(100vw - 32px))",
    height: "44px",
    top: "calc(env(safe-area-inset-top, 0px) + 76px)",
    borderTopLeftRadius: "9999px",
    borderTopRightRadius: "9999px",
    borderBottomLeftRadius: "9999px",
    borderBottomRightRadius: "9999px",
    transition: {
      height:                  { duration: 0.28, ease: APPLE_EASE_OUT },
      width:                   { duration: 0.24, ease: APPLE_EASE_OUT, delay: 0.22 },
      top:                     { duration: 0.30, ease: APPLE_EASE_OUT, delay: 0.16 },
      borderTopLeftRadius:     { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderTopRightRadius:    { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderBottomLeftRadius:  { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderBottomRightRadius: { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
    },
  },
  drawer: {
    width: "100vw",
    height: "88dvh",
    top: "calc(env(safe-area-inset-top, 0px) + 8px)",
    borderTopLeftRadius: "32px",
    borderTopRightRadius: "32px",
    borderBottomLeftRadius: "32px",
    borderBottomRightRadius: "32px",
    transition: {
      top:                     { duration: 0.24, ease: APPLE_EASE_OUT },
      width:                   { duration: 0.26, ease: APPLE_EASE_OUT, delay: 0.04 },
      height:                  { duration: 0.32, ease: APPLE_EASE_OUT, delay: 0.20 },
      borderTopLeftRadius:     { duration: 0.18, ease: APPLE_EASE_OUT },
      borderTopRightRadius:    { duration: 0.18, ease: APPLE_EASE_OUT },
      borderBottomLeftRadius:  { duration: 0.18, ease: APPLE_EASE_OUT },
      borderBottomRightRadius: { duration: 0.18, ease: APPLE_EASE_OUT },
    },
  },
};

// ---------- helpers ---------------------------------------------------
function formatHM(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "—";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
}
function formatDuration(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00Z`);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const isSameDay = (a, b) => a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
  if (isSameDay(d, today)) return "Today";
  if (isSameDay(d, tomorrow)) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// ---------- component -------------------------------------------------
export default function AssignedBlocksSurface({
  blocks = [],
  open,
  onOpenChange,
  onBrowseMarketplace,
  availableCount = 0,
  onChanged,
  hidden = false,
}) {
  const count = blocks.length;

  const start = useCallback(async (id) => {
    try {
      await api.post(`/driver/marketplace/blocks/${id}/start`, {});
      toast.success("Block started");
      onOpenChange?.(false);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object") ? detail.message : formatApiError(detail);
      toast.error(msg || "Could not start block");
    }
  }, [onChanged, onOpenChange]);

  const release = useCallback(async (id) => {
    if (!window.confirm("Release this block? It will return to the marketplace.")) return;
    try {
      await api.post(`/driver/marketplace/blocks/${id}/release`, {});
      toast.success("Block released");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not release block");
    }
  }, [onChanged]);

  // Drag-to-dismiss — top-anchored drawer ⇒ swipe UP to close (origin
  // = top). No scroll guard: the schedule list is typically short
  // (under one screenful) so we don't need to disambiguate scroll vs
  // dismiss. The pill itself is non-draggable; drag is gated on `open`.
  const drag = useDragDismiss({
    onDismiss: () => onOpenChange?.(false),
    origin: "top",
    open,
  });

  if (count === 0) return null;

  // Backdrop when expanded
  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: APPLE_EASE_OUT }}
            className="fixed inset-0 z-40 bg-black/40"
            style={{ opacity: drag.backdropOpacity }}
            onClick={() => onOpenChange?.(false)}
            data-testid="assigned-blocks-backdrop"
          />
        )}
      </AnimatePresence>

      <motion.div
        className={`fixed left-1/2 z-50 bg-[#0A1128] text-white overflow-hidden flex flex-col ${surfaceClass()}`}
        style={{
          x: "-50%",
          y: drag.value,
          transformOrigin: "top center",
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : undefined,
          transition: "opacity 180ms cubic-bezier(0.32, 0.72, 0, 1)",
          ...surfaceStyle("drawer", { theme: "dark" }),
        }}
        variants={AB_VARIANTS}
        initial="pill"
        animate={open ? "drawer" : "pill"}
        onClick={!open && !hidden ? () => onOpenChange?.(true) : undefined}
        role={open ? "dialog" : "button"}
        aria-label={open ? "Assigned blocks" : "Open assigned blocks"}
        aria-modal={open || undefined}
        aria-hidden={hidden || undefined}
        tabIndex={open || hidden ? -1 : 0}
        data-testid={open ? "assigned-blocks-drawer" : "assigned-blocks-pill"}
        data-state={open ? "drawer" : "pill"}
        // Drag only while open — the pill itself must remain tappable
        // (not draggable) so its primary affordance is a tap.
        drag={open ? "y" : false}
        dragConstraints={drag.motionProps.dragConstraints}
        dragElastic={drag.motionProps.dragElastic}
        dragMomentum={drag.motionProps.dragMomentum}
        onDragStart={open ? drag.motionProps.onDragStart : undefined}
        onDrag={open ? drag.motionProps.onDrag : undefined}
        onDragEnd={open ? drag.motionProps.onDragEnd : undefined}
      >
        {/* PILL STATE — collapsed label */}
        {!open && (
          <div className="h-full w-full px-4 flex items-center gap-2 justify-center">
            <Calendar size={14} strokeWidth={2.2} className="text-[#ed6500]" />
            <div className="text-[12px] tracking-[0.18em] uppercase font-semibold">
              {count} Assigned Block{count === 1 ? "" : "s"}
            </div>
          </div>
        )}

        {/* DRAWER STATE — full schedule list */}
        {open && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="relative px-6 pt-6 pb-3 shrink-0">
              <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Schedule</div>
              <h1 className="font-serif font-light text-[28px] leading-tight text-white mt-1">
                {count} Assigned Block{count === 1 ? "" : "s"}
              </h1>
              <div className="text-[12px] text-white/60 mt-1" data-testid="assigned-blocks-subtitle">
                Tap a block to view details, or press Start when you&apos;re ready.
              </div>
              <button
                type="button"
                onClick={() => onOpenChange?.(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 flex items-center justify-center link-pressable"
                aria-label="Close assigned blocks"
                data-testid="assigned-blocks-close"
              >
                <X size={16} strokeWidth={2} className="text-white" />
              </button>
            </div>

            {/* Card list — scrollable */}
            <div
              className="flex-1 overflow-y-auto px-4 pb-4 space-y-3"
              data-testid="assigned-blocks-list"
            >
              {blocks.map((b) => (
                <AssignedBlockCard
                  key={b.id}
                  block={b}
                  onStart={() => start(b.id)}
                  onRelease={() => release(b.id)}
                />
              ))}
            </div>

            {/* Footer — Browse Marketplace */}
            <div className="shrink-0 border-t border-white/10 px-6 py-4 bg-[#0A1128]">
              <button
                type="button"
                onClick={() => { onOpenChange?.(false); onBrowseMarketplace?.(); }}
                className="w-full flex items-center justify-between link-pressable"
                data-testid="assigned-blocks-browse-marketplace"
              >
                <div className="text-left">
                  <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Marketplace</div>
                  <div className="text-white text-[15px] font-medium mt-0.5">
                    {availableCount > 0
                      ? `${availableCount} Available Block${availableCount === 1 ? "" : "s"}`
                      : "Browse open work"}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[12px] tracking-[0.18em] uppercase font-semibold text-white/80">
                  Browse <ChevronRight size={14} strokeWidth={2.2} />
                </div>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
}

// ---------- card ------------------------------------------------------
function AssignedBlockCard({ block, onStart, onRelease }) {
  const legs = Number(block.legs_count || block.stops_count || 0);
  const pay = (block.total_payment_cents != null
    ? block.total_payment_cents
    : block.pay_cents) / 100;
  const time = block.pickup_time || block.window_start;
  const date = formatDate(block.date);
  const status = block.status === "in_progress" ? "In Progress" : "Ready";

  return (
    <div
      className="bg-white/[0.06] hover:bg-white/[0.09] border border-white/10 rounded-2xl p-4 transition-colors"
      data-testid="assigned-block-card"
      data-block-id={block.id}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-white/60">
            <Calendar size={11} strokeWidth={2.2} /> {date} · {formatHM(time)}
          </div>
          <div className="font-serif text-[22px] leading-tight text-white mt-1 truncate">
            ${pay.toFixed(2)}
          </div>
          <div className="text-[12px] text-white/70 mt-0.5 flex items-center gap-1.5">
            <MapPin size={11} strokeWidth={2.2} /> {block.area_label || "—"}
          </div>
        </div>
        <span
          className={`shrink-0 px-2.5 h-6 inline-flex items-center rounded-full text-[10px] tracking-[0.18em] uppercase font-semibold ${
            block.status === "in_progress"
              ? "bg-[#ed6500] text-white"
              : "bg-white/10 text-white/80"
          }`}
          data-testid="assigned-block-status"
        >
          {status}
        </span>
      </div>

      {/* meta row */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-white/60">
        <div className="flex items-center gap-1.5">
          <Layers size={11} strokeWidth={2.2} /> {legs} leg{legs === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-1.5 justify-end">
          <Clock size={11} strokeWidth={2.2} /> {formatDuration(block.estimated_minutes)}
        </div>
      </div>

      {/* actions */}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onStart}
          disabled={block.status === "in_progress"}
          className="flex-1 h-10 rounded-full bg-[#ed6500] text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-1.5 link-pressable disabled:opacity-50"
          data-testid="assigned-block-start"
        >
          <Play size={12} strokeWidth={2.2} fill="currentColor" /> Start Block
        </button>
        <button
          type="button"
          onClick={onRelease}
          disabled={block.status === "in_progress"}
          className="h-10 w-10 rounded-full bg-white/[0.06] border border-white/10 inline-flex items-center justify-center text-white/70 hover:text-white link-pressable disabled:opacity-30"
          aria-label="Release block"
          data-testid="assigned-block-release"
        >
          <Trash2 size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
